import { useState, useMemo, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { AlertCircle, Download, Copy, TrendingUp, Shield, Clock, Target } from "lucide-react";
import { toast } from "sonner";
import { DebugPanel } from "./DebugPanel";

// ============================================================================
// TYPES
// ============================================================================

type Niche = "BeauteSoins" | "ElectroniqueAccessoires" | "MaisonCuisine" | "ComplementsAlimentaires" | "Mode";
type Concurrence = "Forte" | "Moderee" | "Faible" | "BlueOcean";
type BudgetStage = "Minimal" | "Recommande" | "Optimal";
type Saison = "Normale" | "Pic" | "Creuse";
type PromoType = "Coupon" | "Remise" | "Deal";

interface Contexte {
  marqueConnue: boolean;
  niche: Niche;
  concurrence: Concurrence;
  budgetStage: BudgetStage;
  saison: Saison;
}

interface Optimizer {
  prixTTC: number;
  aov: number;
  cvrCat: number;
  cpcCat: number;
  acosCible: number;
  tacosCible: number;
  stockJours: number;
  brandStorePret: boolean;
}

interface Retail {
  buyBoxPct: number;
  priceIndex: number;
  rating: number;
  reviews: number;
  images: number;
  hasAplus: boolean;
}

interface Placements {
  sosACoS: number;
  sosCVR: number;
  rosACoS: number;
  ppACoS: number;
}

interface Promo {
  active: boolean;
  type: PromoType;
  dateDebut: string;
  dateFin: string;
}

interface Daypart {
  spendNightShare: number;
  cvrNightRel: number;
}

interface BudgetCalc {
  budgetJour: number;
  repartition: { sp: number; sb: number; sd: number };
  exp: string;
  debug?: {
    budgetTheo: number;
    budgetStage: number;
    minStage: number;
    maxStage: number;
    capClient: string | number;
    raisonStage: string;
    raisonCap: string;
    m: number;
    clicks: number;
    budgetClicks: number;
    budgetTacos: number;
  };
}

interface Client {
  capJour: number | null;
}

interface BudgetRules {
  respecterBornes: boolean;
  lissage: boolean;
  deltaMaxHebdo: number;
}

interface BudgetHistory {
  prevBudgetJour: number | null;
  prevDateISO: string | null;
}

interface VerrouRetail {
  locked: boolean;
  raison?: string;
}

interface RecoSP {
  budAuto: number;
  budExact: number;
  budPhrase: number;
  budPT: number;
  kwExact: number;
  kwPhrase: number;
  asinPT: number;
  budParAG: number;
  bidInit: number;
  seuilClics: number;
  broadAllowed: boolean;
}

interface RecoSB {
  budSBVideo: number;
  budSBBanniere: number;
  budSBStore: number;
  sbKw: number;
}

interface RecoSD {
  budSDv7_30: number;
  budSDv31_90: number;
  budSDprod: number;
  asinSD: number;
}

interface Reco {
  budget: BudgetCalc;
  verrou: VerrouRetail;
  mode: string;
  sp: RecoSP;
  sb: RecoSB;
  sd: RecoSD;
  triggers: string[];
  placementAdvice: string[];
  dspEligible: boolean;
}

interface Watcher {
  id: string;
  condition: string;
  action: string;
  active: boolean;
}

// ============================================================================
// TABLES DE RÉFÉRENCE
// ============================================================================

const NICHES: Record<Niche, { cpcRef: number; cvrRef: number; tacosRef: number }> = {
  BeauteSoins: { cpcRef: 0.90, cvrRef: 0.10, tacosRef: 0.12 },
  ElectroniqueAccessoires: { cpcRef: 0.70, cvrRef: 0.06, tacosRef: 0.09 },
  MaisonCuisine: { cpcRef: 0.60, cvrRef: 0.08, tacosRef: 0.10 },
  ComplementsAlimentaires: { cpcRef: 1.20, cvrRef: 0.07, tacosRef: 0.16 },
  Mode: { cpcRef: 0.45, cvrRef: 0.03, tacosRef: 0.13 },
};

const CADRES_BUDGET: Record<BudgetStage, { min: number; max: number; repartition: { sp: number; sb: number; sd: number }; ordersCible: number }> = {
  Minimal: { min: 50, max: 80, repartition: { sp: 0.70, sb: 0.25, sd: 0.05 }, ordersCible: 5 },
  Recommande: { min: 120, max: 200, repartition: { sp: 0.65, sb: 0.25, sd: 0.10 }, ordersCible: 10 },
  Optimal: { min: 250, max: 500, repartition: { sp: 0.60, sb: 0.25, sd: 0.15 }, ordersCible: 20 },
};

const REP_SP: Record<Concurrence, { auto: number; exact: number; phrase: number; product: number; broadAllowed?: boolean }> = {
  Forte: { auto: 0.30, exact: 0.35, phrase: 0.20, product: 0.15 },
  Moderee: { auto: 0.25, exact: 0.40, phrase: 0.20, product: 0.15 },
  Faible: { auto: 0.20, exact: 0.35, phrase: 0.25, product: 0.20 },
  BlueOcean: { auto: 0.20, exact: 0.30, phrase: 0.30, product: 0.20, broadAllowed: true },
};

const REP_SB = {
  Base: { video: 0.70, banniere: 0.30, store: 0.00 },
  MarqueOK: { video: 0.50, banniere: 0.25, store: 0.25 },
};

const REP_SD: Record<Concurrence, { views7_30: number; views31_90: number; product: number }> = {
  Forte: { views7_30: 0.55, views31_90: 0.15, product: 0.30 },
  Moderee: { views7_30: 0.60, views31_90: 0.15, product: 0.25 },
  Faible: { views7_30: 0.65, views31_90: 0.10, product: 0.25 },
  BlueOcean: { views7_30: 0.70, views31_90: 0.10, product: 0.20 },
};

const MULTIPLICATEURS = {
  ConcurrenceForte: 1.20,
  ConcurrenceModeree: 1.00,
  ConcurrenceFaible: 0.85,
  BlueOcean: 0.70,
  MarqueConnue: 0.90,
  SaisonPic: 1.25,
  SaisonCreuse: 0.85,
  PromoActive: 1.15,
};

// ============================================================================
// UTILITAIRES
// ============================================================================

const borne = (min: number, max: number, x: number): number => Math.max(min, Math.min(max, x));
const arrondi = (x: number): number => Math.round(x);
const valOr = (v: number | undefined, def: number): number => (v !== undefined && v > 0 ? v : def);
const normalizeNumber = (value: string): number => {
  const normalized = value.replace(",", ".");
  return parseFloat(normalized) || 0;
};

const num = (x: number | string): number => {
  return parseFloat(String(x).replace(",", ".")) || 0;
};

const joursEntre = (aISO: string, bISO: string): number => {
  return Math.abs(new Date(aISO).getTime() - new Date(bISO).getTime()) / 86_400_000;
};

const limiterDeltaHebdo = (prev: number | null, target: number, cap: number): number => {
  if (prev === null) return target;
  const up = prev * (1 + cap);
  const down = prev * (1 - cap);
  return borne(down, up, target);
};

// ============================================================================
// MOTEUR — Calcul Budget V2 avec Cap Client, Bornage et Lissage
// ============================================================================

const calcBudgetV2 = (
  ctx: Contexte,
  eco: Optimizer,
  pr: Promo,
  hist: BudgetHistory,
  rules: BudgetRules,
  cli: Client
): BudgetCalc => {
  // 1) Références de niche
  const ref = NICHES[ctx.niche] || NICHES.BeauteSoins;
  const cvrEff = num(eco.cvrCat) || ref.cvrRef;
  const cpcEff = num(eco.cpcCat) || ref.cpcRef;
  const tacosEff = num(eco.tacosCible) || ref.tacosRef;
  const aovEff = num(eco.aov) || num(eco.prixTTC);

  // 2) Budget théorique marché
  const orders = CADRES_BUDGET[ctx.budgetStage].ordersCible;
  const clicks = orders / Math.max(cvrEff, 0.01);
  const budgetClicks = clicks * cpcEff;
  const budgetTacos = tacosEff * orders * aovEff;

  let m = 1.0;
  if (ctx.concurrence === "Forte") m *= MULTIPLICATEURS.ConcurrenceForte;
  else if (ctx.concurrence === "Moderee") m *= MULTIPLICATEURS.ConcurrenceModeree;
  else if (ctx.concurrence === "Faible") m *= MULTIPLICATEURS.ConcurrenceFaible;
  else if (ctx.concurrence === "BlueOcean") m *= MULTIPLICATEURS.BlueOcean;
  if (ctx.marqueConnue) m *= MULTIPLICATEURS.MarqueConnue;
  if (ctx.saison === "Pic") m *= MULTIPLICATEURS.SaisonPic;
  if (ctx.saison === "Creuse") m *= MULTIPLICATEURS.SaisonCreuse;
  if (pr.active) m *= 1.15;

  const budgetTheo = Math.min(budgetClicks, budgetTacos) * m;

  // 3) Bornes du stage
  const [minStage, maxStage] = [
    CADRES_BUDGET[ctx.budgetStage].min,
    CADRES_BUDGET[ctx.budgetStage].max,
  ];
  const budgetStage = rules.respecterBornes
    ? borne(minStage, maxStage, budgetTheo)
    : budgetTheo;
  
  let raisonStage = "Aucun";
  if (rules.respecterBornes) {
    if (budgetTheo < minStage) raisonStage = "Sous le minimum du stage";
    else if (budgetTheo > maxStage) raisonStage = "Au dessus du maximum du stage";
  } else {
    raisonStage = "Bornage désactivé";
  }

  // 4) Cap client
  const budgetCap = cli.capJour ? Math.min(budgetStage, num(cli.capJour)) : budgetStage;
  const raisonCap =
    cli.capJour && num(cli.capJour) < budgetStage
      ? "Plafonné par le cap client"
      : "Aucun";

  // 5) Lissage et delta max hebdo
  let target = budgetCap;
  let lisse = target;
  if (rules.lissage) {
    // Si la dernière mise à jour date de > 7 jours, on réinitialise le prev
    if (hist.prevDateISO && joursEntre(hist.prevDateISO, new Date().toISOString()) > 7) {
      hist.prevBudgetJour = null;
    }
    const lim = limiterDeltaHebdo(hist.prevBudgetJour, target, rules.deltaMaxHebdo);
    lisse = hist.prevBudgetJour === null ? lim : 0.7 * hist.prevBudgetJour + 0.3 * lim;
  }
  const budgetJour = Math.round(lisse);

  // Mettre à jour l'historique
  hist.prevBudgetJour = budgetJour;
  hist.prevDateISO = new Date().toISOString();

  // 6) Répartition
  const parts = CADRES_BUDGET[ctx.budgetStage].repartition;

  return {
    budgetJour,
    repartition: parts,
    exp: `Niche — ${ctx.niche} — CPC ${cpcEff.toFixed(2)} € — CVR ${(cvrEff * 100).toFixed(1)} % — TACoS ${(tacosEff * 100).toFixed(0)} % — ×${m.toFixed(2)}`,
    debug: {
      budgetTheo: Math.round(budgetTheo),
      budgetStage: Math.round(budgetStage),
      minStage,
      maxStage,
      capClient: cli.capJour ?? "non défini",
      raisonStage,
      raisonCap,
      m: +m.toFixed(2),
      clicks: Math.round(clicks),
      budgetClicks: Math.round(budgetClicks),
      budgetTacos: Math.round(budgetTacos),
    },
  };
};

// ============================================================================
// GARDE FOU RETAIL
// ============================================================================

const lockRetail = (retail: Retail): VerrouRetail => {
  if (retail.buyBoxPct < 0.92) {
    return { locked: true, raison: "Buy box sous 92 %" };
  }
  if (retail.rating < 4.2 || retail.reviews < 50) {
    return { locked: true, raison: "Preuve sociale insuffisante" };
  }
  if (retail.images < 6 || !retail.hasAplus) {
    return { locked: true, raison: "Retail readiness incomplet" };
  }
  return { locked: false };
};

// ============================================================================
// MOTEUR — Recommandation SP — SB — SD
// ============================================================================

const computeReco = (
  ctx: Contexte,
  eco: Optimizer,
  ret: Retail,
  plc: Placements,
  pr: Promo,
  dp: Daypart,
  hist: BudgetHistory,
  rules: BudgetRules,
  cli: Client
): Reco => {
  const budget = calcBudgetV2(ctx, eco, pr, hist, rules, cli);
  const verrou = lockRetail(ret);
  const parts = budget.repartition;
  const budgetSP = budget.budgetJour * parts.sp;
  const budgetSB = budget.budgetJour * parts.sb;
  const budgetSD = budget.budgetJour * parts.sd;

  // SP
  const pSP = REP_SP[ctx.concurrence];
  const budAuto = budgetSP * pSP.auto;
  const budExact = budgetSP * pSP.exact;
  const budPhrase = budgetSP * pSP.phrase;
  const budPT = budgetSP * pSP.product;
  const kwExact = borne(8, 60, arrondi((budExact * 7) / (10 * Math.max(eco.cpcCat, 0.2))));
  const kwPhrase = borne(8, 60, arrondi((budPhrase * 7) / (10 * Math.max(eco.cpcCat, 0.2))));
  const asinPT = borne(10, 80, arrondi((budPT * 7) / (10 * Math.max(eco.cpcCat, 0.2))));
  const budParAG = borne(3, 25, budAuto / 4);
  const bidInit = eco.prixTTC * eco.cvrCat * eco.acosCible;
  const seuilClics = Math.max(20, arrondi(25 / Math.max(eco.cvrCat, 0.01)));
  const broadAllowed = pSP.broadAllowed || false;

  const sp: RecoSP = {
    budAuto,
    budExact,
    budPhrase,
    budPT,
    kwExact,
    kwPhrase,
    asinPT,
    budParAG,
    bidInit,
    seuilClics,
    broadAllowed,
  };

  // SB
  const pSB = eco.brandStorePret ? REP_SB.MarqueOK : REP_SB.Base;
  const budSBVideo = budgetSB * pSB.video;
  const budSBBanniere = budgetSB * pSB.banniere;
  const budSBStore = budgetSB * pSB.store;
  const sbKw = borne(3, 12, arrondi(budSBVideo / Math.max(eco.cpcCat, 0.3)));

  const sb: RecoSB = {
    budSBVideo,
    budSBBanniere,
    budSBStore,
    sbKw,
  };

  // SD
  const pSD = REP_SD[ctx.concurrence];
  const budSDv7_30 = budgetSD * pSD.views7_30;
  const budSDv31_90 = budgetSD * pSD.views31_90;
  const budSDprod = budgetSD * pSD.product;
  const asinSD = borne(10, 60, arrondi((budSDprod * 7) / (10 * Math.max(eco.cpcCat, 0.2))));

  const sd: RecoSD = {
    budSDv7_30,
    budSDv31_90,
    budSDprod,
    asinSD,
  };

  // Mode
  const mode = ctx.marqueConnue ? "Défense et conquête" : "Conquête structurée";

  // Triggers
  const triggers: string[] = [];
  triggers.push("Activer Top of Search si CVR > médiane et ACoS à la cible");
  if (eco.stockJours < 15) {
    triggers.push("⚠️ Geler le scale — stock < 15 jours");
  } else {
    triggers.push("✓ Stock suffisant pour scaler");
  }
  if (ctx.marqueConnue) {
    triggers.push("Déployer SP Brand et SB Marque en défense");
  } else {
    triggers.push("Accent découverte et retargeting");
  }

  // Placements advice
  const placementAdvice: string[] = [];
  if (plc.sosACoS <= eco.acosCible && plc.sosCVR > 0.08) {
    placementAdvice.push("✓ Renforcer Top of Search — +20 à +40 %");
  }
  if (plc.rosACoS < eco.acosCible * 0.8 && plc.ppACoS > eco.acosCible * 1.2) {
    placementAdvice.push("Déplacer budget de Product Pages vers Rest of Search");
  }

  // Daypart
  if (dp.spendNightShare > 0.25 && dp.cvrNightRel < 0.70) {
    placementAdvice.push("Appliquer −20 % enchères sur tranches nuit");
  }

  // Promo
  if (pr.active) {
    placementAdvice.push("Relever ACoS cible de +15 % pendant la promo — revert 7j après fin");
  }

  // DSP
  const dspEligible = budget.budgetJour >= 800;

  return {
    budget,
    verrou,
    mode,
    sp,
    sb,
    sd,
    triggers,
    placementAdvice,
    dspEligible,
  };
};

// ============================================================================
// WATCHERS
// ============================================================================

const WATCHERS: Omit<Watcher, "active">[] = [
  {
    id: "ctr_low",
    condition: "CTR global < 0.25 % et impressions > 1 000",
    action: "Retravailler titres et visuels plutôt que la bid",
  },
  {
    id: "no_conv",
    condition: "Clics ≥ 20 et conversions = 0",
    action: "Baisser enchères −15 % et ajouter négatifs",
  },
  {
    id: "acos_low",
    condition: "ACoS ≤ cible −10 %",
    action: "+10 % d'enchère ou élargir Exact et Phrase",
  },
  {
    id: "sov_low",
    condition: "SoV < objectif et ACoS ok",
    action: "+20 % Top of Search",
  },
  {
    id: "stock_low",
    condition: "Stock < 15 jours",
    action: "Stop scale et basculer sur maintien",
  },
  {
    id: "buybox_low",
    condition: "Buy box < 92 %",
    action: "Geler scale et prioriser défense marque",
  },
  {
    id: "rating_low",
    condition: "Note < 4.2 ou avis < 50",
    action: "Plafonner CPC à CPC seuil × 0.8 et accélérer collecte d'avis",
  },
  {
    id: "budget_limited",
    condition: "Campagne limitée par budget deux jours",
    action: "Migrer sous budget portefeuille +25 %",
  },
  {
    id: "sb_ctr_low",
    condition: "SB vidéo CTR < 0.6 % sur 1 000 vues",
    action: "Refaire hook et miniature — ne pas augmenter la bid",
  },
  {
    id: "sd_freq_high",
    condition: "SD fréquence > 6 et ROAS en baisse trois jours",
    action: "Élargir produit ou réduire fenêtre 31 à 90",
  },
  {
    id: "promo_active",
    condition: "Promo active",
    action: "Relever ACoS cible de +15 % et revert 7j après fin",
  },
  {
    id: "night_inefficient",
    condition: "Dépense nuit > 25 % et CVR nuit < 70 % du jour",
    action: "−20 % enchères nuit",
  },
];

// ============================================================================
// GLOSSAIRE
// ============================================================================

const GLOSSAIRE = [
  { term: "SP", definition: "Produits sponsorisés — publicité sur requêtes et produits" },
  { term: "SB", definition: "Marques sponsorisées — bannière et vidéo pour capter la demande" },
  { term: "SD", definition: "Display sponsorisé — retargeting et ciblage produit" },
  { term: "Top of Search", definition: "Emplacement haut de page" },
  { term: "Rest of Search", definition: "Résultats hors haut de page" },
  { term: "Product Pages", definition: "Emplacements sur pages produits" },
  { term: "PDP", definition: "Page détail produit" },
  { term: "Exact", definition: "Correspondance exacte" },
  { term: "Phrase", definition: "Correspondance expression" },
  { term: "Broad", definition: "Correspondance large contrôlée avec négatifs" },
  { term: "Négatifs", definition: "Requêtes ou ASIN exclus" },
  { term: "Buy box", definition: "Part de possession de la boîte d'achat" },
  { term: "Price index", definition: "Ratio de prix vs concurrent principal" },
  { term: "CPC seuil", definition: "CPC de rentabilité — Prix HT × CVR × ACoS cible" },
  { term: "TACoS", definition: "Dépense publicitaire ÷ chiffre d'affaires total" },
  { term: "SoV", definition: "Part de voix organique et sponsorisée" },
  { term: "NTB %", definition: "Part de nouveaux acheteurs" },
  { term: "Harvest", definition: "Transfert des requêtes gagnantes vers Manuel" },
  { term: "Pruning", definition: "Arrêt des requêtes ou ASIN à perte" },
  { term: "Budget de portefeuille", definition: "Plafond partagé multi campagnes" },
  { term: "Fenêtre d'attribution", definition: "Période de comptabilisation des conversions" },
  { term: "Dayparting", definition: "Modulation d'enchères par tranche horaire" },
  { term: "DSP", definition: "Demand Side Platform — achat média display Amazon" },
];

// ============================================================================
// COMPOSANT PRINCIPAL
// ============================================================================

export const PlaybookPageV2 = () => {
  // Valeurs initiales pour le reset
  const initialContexte: Contexte = {
    marqueConnue: false,
    niche: "BeauteSoins",
    concurrence: "Moderee",
    budgetStage: "Recommande",
    saison: "Normale",
  };

  const initialOptimizer: Optimizer = {
    prixTTC: 25,
    aov: 25,
    cvrCat: 0.08,
    cpcCat: 0.75,
    acosCible: 0.20,
    tacosCible: 0.12,
    stockJours: 30,
    brandStorePret: false,
  };

  const initialPromo: Promo = {
    active: false,
    type: "Coupon",
    dateDebut: "",
    dateFin: "",
  };

  // États Contexte
  const [contexte, setContexte] = useState<Contexte>(initialContexte);

  // États Optimizer
  const [optimizer, setOptimizer] = useState<Optimizer>(initialOptimizer);

  // États Retail
  const [retail, setRetail] = useState<Retail>({
    buyBoxPct: 0.95,
    priceIndex: 1.0,
    rating: 4.5,
    reviews: 150,
    images: 7,
    hasAplus: true,
  });

  // États Placements
  const [placements, setPlacements] = useState<Placements>({
    sosACoS: 0.18,
    sosCVR: 0.09,
    rosACoS: 0.22,
    ppACoS: 0.25,
  });

  // États Promo
  const [promo, setPromo] = useState<Promo>(initialPromo);

  // États Daypart
  const [daypart, setDaypart] = useState<Daypart>({
    spendNightShare: 0.15,
    cvrNightRel: 0.85,
  });

  // États Watchers
  const [watchers, setWatchers] = useState<Watcher[]>(
    WATCHERS.map((w) => ({ ...w, active: true }))
  );

  // États Budget dynamique
  const [client, setClient] = useState<Client>({ capJour: null });
  const [budgetRules, setBudgetRules] = useState<BudgetRules>({
    respecterBornes: true,
    lissage: true,
    deltaMaxHebdo: 0.25,
  });
  const [budgetHistory] = useState<BudgetHistory>({
    prevBudgetJour: null,
    prevDateISO: null,
  });

  // Calcul réactif de la reco
  const reco = useMemo(
    () => computeReco(contexte, optimizer, retail, placements, promo, daypart, budgetHistory, budgetRules, client),
    [contexte, optimizer, retail, placements, promo, daypart, budgetHistory, budgetRules, client]
  );

  // Handlers avec debounce
  const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(null);

  const handleContexteChange = useCallback((key: keyof Contexte, value: any) => {
    setContexte((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleOptimizerChange = useCallback((key: keyof Optimizer, value: any) => {
    if (debounceTimer) clearTimeout(debounceTimer);
    const timer = setTimeout(() => {
      setOptimizer((prev) => ({ ...prev, [key]: value }));
    }, 200);
    setDebounceTimer(timer);
  }, [debounceTimer]);

  const handleRetailChange = useCallback((key: keyof Retail, value: any) => {
    if (debounceTimer) clearTimeout(debounceTimer);
    const timer = setTimeout(() => {
      setRetail((prev) => ({ ...prev, [key]: value }));
    }, 200);
    setDebounceTimer(timer);
  }, [debounceTimer]);

  const handlePlacementsChange = useCallback((key: keyof Placements, value: any) => {
    if (debounceTimer) clearTimeout(debounceTimer);
    const timer = setTimeout(() => {
      setPlacements((prev) => ({ ...prev, [key]: value }));
    }, 200);
    setDebounceTimer(timer);
  }, [debounceTimer]);

  const handlePromoChange = useCallback((key: keyof Promo, value: any) => {
    setPromo((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleDaypartChange = useCallback((key: keyof Daypart, value: any) => {
    if (debounceTimer) clearTimeout(debounceTimer);
    const timer = setTimeout(() => {
      setDaypart((prev) => ({ ...prev, [key]: value }));
    }, 200);
    setDebounceTimer(timer);
  }, [debounceTimer]);

  const toggleWatcher = (id: string) => {
    setWatchers((prev) =>
      prev.map((w) => (w.id === id ? { ...w, active: !w.active } : w))
    );
  };

  const handleReset = () => {
    setContexte(initialContexte);
    setOptimizer(initialOptimizer);
    setPromo(initialPromo);
    toast.success("Valeurs réinitialisées");
  };

  const exportCSV = () => {
    toast.success("Export CSV en cours...");
  };

  const exportPDF = () => {
    toast.success("Export PDF en cours...");
  };

  const copyPlaybook = () => {
    const text = `
Playbook Amazon Ads — ${contexte.niche}
Mode — ${reco.mode}
Budget — ${reco.budget.budgetJour}€/j
Répartition — SP ${(reco.budget.repartition.sp * 100).toFixed(0)}% — SB ${(reco.budget.repartition.sb * 100).toFixed(0)}% — SD ${(reco.budget.repartition.sd * 100).toFixed(0)}%

SP Auto — ${arrondi(reco.sp.budParAG)}€/groupe × 4 ad groups
SP Exact — ${reco.sp.kwExact} mots clés — enchère ${reco.sp.bidInit.toFixed(2)}€
SP Phrase — ${reco.sp.kwPhrase} mots clés
SP Product — ${reco.sp.asinPT} ASIN

SB Vidéo — ${arrondi(reco.sb.budSBVideo)}€/j — ${reco.sb.sbKw} mots clés
SB Bannière — ${arrondi(reco.sb.budSBBanniere)}€/j
SB Store — ${arrondi(reco.sb.budSBStore)}€/j

SD Views 7-30j — ${arrondi(reco.sd.budSDv7_30)}€/j
SD Views 31-90j — ${arrondi(reco.sd.budSDv31_90)}€/j
SD Product — ${reco.sd.asinSD} ASIN — ${arrondi(reco.sd.budSDprod)}€/j
    `.trim();
    navigator.clipboard.writeText(text);
    toast.success("Playbook copié dans le presse-papiers");
  };

  return (
    <div className="space-y-6 p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Playbook Amazon Ads</h1>
          <p className="text-muted-foreground">Recommandations dynamiques SP — SB — SD</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={copyPlaybook} variant="outline" size="sm">
            <Copy className="w-4 h-4 mr-2" />
            Copier
          </Button>
          <Button onClick={exportCSV} variant="outline" size="sm">
            <Download className="w-4 h-4 mr-2" />
            CSV
          </Button>
          <Button onClick={exportPDF} variant="outline" size="sm">
            <Download className="w-4 h-4 mr-2" />
            PDF
          </Button>
        </div>
      </div>

      {/* Section 1 — Contexte */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Target className="w-5 h-5" />
            Contexte
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Niche</Label>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant={contexte.niche === "BeauteSoins" ? "default" : "outline"}
                  size="sm"
                  onClick={() => handleContexteChange("niche", "BeauteSoins")}
                >
                  Beauté & Soins
                </Button>
                <Button
                  variant={contexte.niche === "ElectroniqueAccessoires" ? "default" : "outline"}
                  size="sm"
                  onClick={() => handleContexteChange("niche", "ElectroniqueAccessoires")}
                >
                  Électronique & Accessoires
                </Button>
                <Button
                  variant={contexte.niche === "MaisonCuisine" ? "default" : "outline"}
                  size="sm"
                  onClick={() => handleContexteChange("niche", "MaisonCuisine")}
                >
                  Maison & Cuisine
                </Button>
                <Button
                  variant={contexte.niche === "ComplementsAlimentaires" ? "default" : "outline"}
                  size="sm"
                  onClick={() => handleContexteChange("niche", "ComplementsAlimentaires")}
                >
                  Compléments alimentaires
                </Button>
                <Button
                  variant={contexte.niche === "Mode" ? "default" : "outline"}
                  size="sm"
                  onClick={() => handleContexteChange("niche", "Mode")}
                >
                  Mode
                </Button>
              </div>
            </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">

            <div className="space-y-2">
              <Label>Niveau de concurrence</Label>
              <Select
                value={contexte.concurrence}
                onValueChange={(v) => handleContexteChange("concurrence", v as Concurrence)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Forte">Forte</SelectItem>
                  <SelectItem value="Moderee">Modérée</SelectItem>
                  <SelectItem value="Faible">Faible</SelectItem>
                  <SelectItem value="BlueOcean">Blue Ocean</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Niveau de budget</Label>
              <Select
                value={contexte.budgetStage}
                onValueChange={(v) => handleContexteChange("budgetStage", v as BudgetStage)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Minimal">Minimal (50-80€/j)</SelectItem>
                  <SelectItem value="Recommande">Recommandé (120-200€/j)</SelectItem>
                  <SelectItem value="Optimal">Optimal (250-500€/j)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Saison</Label>
              <Select
                value={contexte.saison}
                onValueChange={(v) => handleContexteChange("saison", v as Saison)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Normale">Normale</SelectItem>
                  <SelectItem value="Pic">Pic</SelectItem>
                  <SelectItem value="Creuse">Creuse</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                id="marqueConnue"
                checked={contexte.marqueConnue}
                onCheckedChange={(v) => handleContexteChange("marqueConnue", v)}
              />
              <Label htmlFor="marqueConnue">Marque connue</Label>
            </div>
          </div>
          </div>
        </CardContent>
      </Card>

      {/* Section 2 — Paramètres économiques */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Paramètres économiques</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>Prix TTC (€)</Label>
              <Input
                type="text"
                value={optimizer.prixTTC}
                onChange={(e) => handleOptimizerChange("prixTTC", normalizeNumber(e.target.value))}
              />
            </div>
            <div className="space-y-2">
              <Label>AOV (€)</Label>
              <Input
                type="text"
                value={optimizer.aov}
                onChange={(e) => handleOptimizerChange("aov", normalizeNumber(e.target.value))}
              />
            </div>
            <div className="space-y-2">
              <Label>CVR catégorie (%)</Label>
              <Input
                type="text"
                value={(optimizer.cvrCat * 100).toFixed(2)}
                onChange={(e) => handleOptimizerChange("cvrCat", normalizeNumber(e.target.value) / 100)}
              />
            </div>
            <div className="space-y-2">
              <Label>CPC catégorie (€)</Label>
              <Input
                type="text"
                value={optimizer.cpcCat.toFixed(2)}
                onChange={(e) => handleOptimizerChange("cpcCat", normalizeNumber(e.target.value))}
              />
            </div>
            <div className="space-y-2">
              <Label>ACoS cible (%)</Label>
              <Input
                type="text"
                value={(optimizer.acosCible * 100).toFixed(0)}
                onChange={(e) => handleOptimizerChange("acosCible", normalizeNumber(e.target.value) / 100)}
              />
            </div>
            <div className="space-y-2">
              <Label>TACoS cible (%)</Label>
              <Input
                type="text"
                value={(optimizer.tacosCible * 100).toFixed(0)}
                onChange={(e) => handleOptimizerChange("tacosCible", normalizeNumber(e.target.value) / 100)}
              />
            </div>
            <div className="space-y-2">
              <Label>Stock (jours)</Label>
              <Input
                type="number"
                value={optimizer.stockJours}
                onChange={(e) => handleOptimizerChange("stockJours", parseInt(e.target.value))}
              />
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="brandStorePret"
                checked={optimizer.brandStorePret}
                onCheckedChange={(v) => handleOptimizerChange("brandStorePret", v)}
              />
              <Label htmlFor="brandStorePret">Store prêt</Label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Section 3 — Retail readiness */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Retail readiness et buy box
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Buy box (%)</Label>
              <Input
                type="text"
                value={(retail.buyBoxPct * 100).toFixed(0)}
                onChange={(e) => handleRetailChange("buyBoxPct", normalizeNumber(e.target.value) / 100)}
              />
            </div>
            <div className="space-y-2">
              <Label>Prix index</Label>
              <Input
                type="text"
                value={retail.priceIndex.toFixed(2)}
                onChange={(e) => handleRetailChange("priceIndex", normalizeNumber(e.target.value))}
              />
            </div>
            <div className="space-y-2">
              <Label>Note</Label>
              <Input
                type="text"
                value={retail.rating.toFixed(1)}
                onChange={(e) => handleRetailChange("rating", normalizeNumber(e.target.value))}
              />
            </div>
            <div className="space-y-2">
              <Label>Nombre d'avis</Label>
              <Input
                type="number"
                value={retail.reviews}
                onChange={(e) => handleRetailChange("reviews", parseInt(e.target.value))}
              />
            </div>
            <div className="space-y-2">
              <Label>Nombre d'images</Label>
              <Input
                type="number"
                value={retail.images}
                onChange={(e) => handleRetailChange("images", parseInt(e.target.value))}
              />
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="hasAplus"
                checked={retail.hasAplus}
                onCheckedChange={(v) => handleRetailChange("hasAplus", v)}
              />
              <Label htmlFor="hasAplus">A+ présent</Label>
            </div>
          </div>

          {reco.verrou.locked && (
            <div className="flex items-start gap-2 p-4 bg-destructive/10 border border-destructive rounded-lg">
              <AlertCircle className="w-5 h-5 text-destructive mt-0.5" />
              <div>
                <p className="font-semibold text-destructive">Alerte — Geler le scale</p>
                <p className="text-sm text-muted-foreground">{reco.verrou.raison}</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Mode debug */}
      <DebugPanel
        contexte={contexte}
        optimizer={optimizer}
        retail={retail}
        placements={placements}
        promo={promo}
        daypart={daypart}
        reco={reco}
        onContexteChange={handleContexteChange}
        onOptimizerChange={handleOptimizerChange}
        onPromoChange={handlePromoChange}
        onReset={handleReset}
      />

      {/* Section 4 — Budget calculé */}
      <Card className="border-primary">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Budget calculé
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Contrôles */}
          <div className="flex flex-wrap gap-4 items-center justify-end mb-4">
            <div className="flex items-center gap-2">
              <Label htmlFor="capJour" className="text-sm">Cap client (€)</Label>
              <Input
                id="capJour"
                type="text"
                placeholder="aucun"
                value={client.capJour ?? ""}
                onChange={(e) =>
                  setClient({ capJour: e.target.value ? normalizeNumber(e.target.value) : null })
                }
                className="w-24"
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                id="respecterBornes"
                checked={budgetRules.respecterBornes}
                onCheckedChange={(v) => setBudgetRules((prev) => ({ ...prev, respecterBornes: v }))}
              />
              <Label htmlFor="respecterBornes" className="text-sm">Respecter les bornes du stage</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                id="lissage"
                checked={budgetRules.lissage}
                onCheckedChange={(v) => setBudgetRules((prev) => ({ ...prev, lissage: v }))}
              />
              <Label htmlFor="lissage" className="text-sm" title="Delta max hebdo ±25 % — lissage 70 % ancien, 30 % cible">
                Lissage
              </Label>
            </div>
          </div>

          {/* Tuiles */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-primary/10 rounded-lg">
              <p className="text-sm text-muted-foreground">Budget jour</p>
              <p className="text-3xl font-bold">{reco.budget.budgetJour}€</p>
            </div>
            <div className="text-center p-4 bg-secondary/10 rounded-lg">
              <p className="text-sm text-muted-foreground">Mode</p>
              <p className="text-xl font-semibold">{reco.mode}</p>
            </div>
            <div className="text-center p-4 bg-accent/10 rounded-lg">
              <p className="text-sm text-muted-foreground">Répartition</p>
              <div className="flex gap-2 justify-center mt-2">
                <Badge>SP {(reco.budget.repartition.sp * 100).toFixed(0)}%</Badge>
                <Badge>SB {(reco.budget.repartition.sb * 100).toFixed(0)}%</Badge>
                <Badge>SD {(reco.budget.repartition.sd * 100).toFixed(0)}%</Badge>
              </div>
            </div>
          </div>

          {/* Chips Debug */}
          {reco.budget.debug && (
            <div className="flex flex-wrap gap-2 mt-4">
              <Badge variant="outline">Budget théorique — {reco.budget.debug.budgetTheo}€</Badge>
              <Badge variant="outline">
                Budget borné stage — {reco.budget.debug.budgetStage}€ 〔{reco.budget.debug.minStage} — {reco.budget.debug.maxStage}〕
              </Badge>
              <Badge variant="outline">Cap client — {reco.budget.debug.capClient}</Badge>
              <Badge
                variant="outline"
                className={
                  reco.budget.debug.raisonCap !== "Aucun" || reco.budget.debug.raisonStage !== "Aucun"
                    ? reco.budget.debug.raisonCap === "Plafonné par le cap client" || reco.budget.debug.raisonStage === "Au dessus du maximum du stage"
                      ? "bg-destructive/10 text-destructive"
                      : "bg-amber-500/10 text-amber-600"
                    : "bg-green-500/10 text-green-600"
                }
              >
                Raison du bornage — {reco.budget.debug.raisonCap !== "Aucun" ? reco.budget.debug.raisonCap : reco.budget.debug.raisonStage}
              </Badge>
              <Badge variant="outline">Multiplicateur m — {reco.budget.debug.m}</Badge>
            </div>
          )}

          <p className="text-sm text-muted-foreground text-center">{reco.budget.exp}</p>
          {optimizer.stockJours < 15 && (
            <div className="flex items-start gap-2 p-4 bg-destructive/10 border border-destructive rounded-lg">
              <AlertCircle className="w-5 h-5 text-destructive mt-0.5" />
              <p className="text-sm">
                <span className="font-semibold">Alerte stock —</span> Stock insuffisant ({optimizer.stockJours} jours) — geler le scale
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Section 5 — Recommandations SP — SB — SD */}
      <Accordion type="single" collapsible className="space-y-4">
        {/* SP */}
        <AccordionItem value="sp" className="border rounded-lg px-4">
          <AccordionTrigger className="hover:no-underline">
            <div className="flex items-center gap-2">
              <Badge variant="default">SP</Badge>
              <span className="font-semibold">Produits sponsorisés — {arrondi(reco.budget.budgetJour * reco.budget.repartition.sp)}€/j</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="space-y-3 pt-4">
            <div className="space-y-2">
              <p className="font-semibold text-sm">SP Auto — 4 ad groups</p>
              <p className="text-sm text-muted-foreground">
                Close Match — Loose Match — Substitutes — Complements — budget ~ {arrondi(reco.sp.budParAG)}€/groupe — +20 % sur Close si CVR &gt; médiane après 7 jours — négatifs hebdo
              </p>
            </div>
            <div className="space-y-2">
              <p className="font-semibold text-sm">SP Manual Exact — {reco.sp.kwExact} mots clés cœur</p>
              <p className="text-sm text-muted-foreground">
                Enchère initiale ~ {reco.sp.bidInit.toFixed(2)}€ — si ACoS &gt; cible +20 % sur {reco.sp.seuilClics} clics puis baisse 10 à 15 %
              </p>
            </div>
            <div className="space-y-2">
              <p className="font-semibold text-sm">SP Manual Phrase — {reco.sp.kwPhrase} mots clés cœur</p>
              <p className="text-sm text-muted-foreground">
                Si pas d'impressions &lt; 100 — +15 à +25 % ou élargir en Broad contrôlé
                {reco.sp.broadAllowed && <span className="text-primary font-semibold"> — Broad autorisé en Blue Ocean</span>}
              </p>
            </div>
            <div className="space-y-2">
              <p className="font-semibold text-sm">SP Product concurrents — {reco.sp.asinPT} ASIN</p>
              <p className="text-sm text-muted-foreground">
                Prioriser PDP faibles — exclure ses ASIN
              </p>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* SB */}
        <AccordionItem value="sb" className="border rounded-lg px-4">
          <AccordionTrigger className="hover:no-underline">
            <div className="flex items-center gap-2">
              <Badge variant="secondary">SB</Badge>
              <span className="font-semibold">Marques sponsorisées — {arrondi(reco.budget.budgetJour * reco.budget.repartition.sb)}€/j</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="space-y-3 pt-4">
            <div className="space-y-2">
              <p className="font-semibold text-sm">SB Vidéo — {reco.sb.sbKw} mots clés cœur</p>
              <p className="text-sm text-muted-foreground">
                Budget ~ {arrondi(reco.sb.budSBVideo)}€/j — hook &lt; 2s — bénéfice clair — sous titres — si CTR vidéo &lt; 0.6 % après 1 000 vues — refaire le hook
              </p>
              <p className="text-xs text-muted-foreground">
                Conformité — durée 6 à 15s — sous titres — promesse non médicale — logo visible en 1s
              </p>
            </div>
            <div className="space-y-2">
              <p className="font-semibold text-sm">SB Bannière</p>
              <p className="text-sm text-muted-foreground">
                Budget ~ {arrondi(reco.sb.budSBBanniere)}€/j — requêtes marque et catégorie — placements haut si CVR &gt; médiane
              </p>
            </div>
            <div className="space-y-2">
              <p className="font-semibold text-sm">SB Store Spotlight</p>
              {optimizer.brandStorePret ? (
                <p className="text-sm text-muted-foreground">
                  Budget ~ {arrondi(reco.sb.budSBStore)}€/j — A/B créas et Store Spotlight actifs
                </p>
              ) : (
                <p className="text-sm text-muted-foreground text-amber-600">
                  Non prioritaire tant que Store non prêt
                </p>
              )}
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* SD */}
        <AccordionItem value="sd" className="border rounded-lg px-4">
          <AccordionTrigger className="hover:no-underline">
            <div className="flex items-center gap-2">
              <Badge variant="outline">SD</Badge>
              <span className="font-semibold">Display sponsorisé — {arrondi(reco.budget.budgetJour * reco.budget.repartition.sd)}€/j</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="space-y-3 pt-4">
            <div className="space-y-2">
              <p className="font-semibold text-sm">SD Views 7 à 30 jours</p>
              <p className="text-sm text-muted-foreground">
                Budget ~ {arrondi(reco.sd.budSDv7_30)}€/j — exclure acheteurs 90 jours — objectif — conversion d'appoint
              </p>
            </div>
            <div className="space-y-2">
              <p className="font-semibold text-sm">SD Views 31 à 90 jours</p>
              <p className="text-sm text-muted-foreground">
                Budget ~ {arrondi(reco.sd.budSDv31_90)}€/j — fréquence modérée — relance froide
              </p>
            </div>
            <div className="space-y-2">
              <p className="font-semibold text-sm">SD Product concurrents — {reco.sd.asinSD} ASIN</p>
              <p className="text-sm text-muted-foreground">
                Budget ~ {arrondi(reco.sd.budSDprod)}€/j — pages concurrents à faiblesse visible
              </p>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      {/* Triggers */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Déclencheurs de scale</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2">
            {reco.triggers.map((trigger, i) => (
              <li key={i} className="text-sm flex items-start gap-2">
                <span className="text-primary">•</span>
                <span>{trigger}</span>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      {/* Placements et portefeuilles */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Placements et portefeuilles</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Top of Search — ACoS (%)</Label>
              <Input
                type="text"
                value={(placements.sosACoS * 100).toFixed(0)}
                onChange={(e) => handlePlacementsChange("sosACoS", normalizeNumber(e.target.value) / 100)}
              />
            </div>
            <div className="space-y-2">
              <Label>Top of Search — CVR (%)</Label>
              <Input
                type="text"
                value={(placements.sosCVR * 100).toFixed(1)}
                onChange={(e) => handlePlacementsChange("sosCVR", normalizeNumber(e.target.value) / 100)}
              />
            </div>
            <div className="space-y-2">
              <Label>Rest of Search — ACoS (%)</Label>
              <Input
                type="text"
                value={(placements.rosACoS * 100).toFixed(0)}
                onChange={(e) => handlePlacementsChange("rosACoS", normalizeNumber(e.target.value) / 100)}
              />
            </div>
            <div className="space-y-2">
              <Label>Product Pages — ACoS (%)</Label>
              <Input
                type="text"
                value={(placements.ppACoS * 100).toFixed(0)}
                onChange={(e) => handlePlacementsChange("ppACoS", normalizeNumber(e.target.value) / 100)}
              />
            </div>
          </div>

          {reco.placementAdvice.length > 0 && (
            <div className="space-y-2 p-4 bg-primary/5 rounded-lg">
              <p className="font-semibold text-sm">Conseils de réglage</p>
              <ul className="space-y-1">
                {reco.placementAdvice.map((advice, i) => (
                  <li key={i} className="text-sm flex items-start gap-2">
                    <span className="text-primary">→</span>
                    <span>{advice}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Promo */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Promo</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center space-x-2">
            <Switch
              id="promoActive"
              checked={promo.active}
              onCheckedChange={(v) => handlePromoChange("active", v)}
            />
            <Label htmlFor="promoActive">Promo active</Label>
          </div>
          {promo.active && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Type</Label>
                <Select
                  value={promo.type}
                  onValueChange={(v) => handlePromoChange("type", v as PromoType)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Coupon">Coupon</SelectItem>
                    <SelectItem value="Remise">Remise</SelectItem>
                    <SelectItem value="Deal">Deal</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Date début</Label>
                <Input
                  type="date"
                  value={promo.dateDebut}
                  onChange={(e) => handlePromoChange("dateDebut", e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Date fin</Label>
                <Input
                  type="date"
                  value={promo.dateFin}
                  onChange={(e) => handlePromoChange("dateFin", e.target.value)}
                />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Daypart */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Dayparting
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Part dépense nuit (%)</Label>
              <Input
                type="text"
                value={(daypart.spendNightShare * 100).toFixed(0)}
                onChange={(e) => handleDaypartChange("spendNightShare", normalizeNumber(e.target.value) / 100)}
              />
            </div>
            <div className="space-y-2">
              <Label>CVR nuit relative (%)</Label>
              <Input
                type="text"
                value={(daypart.cvrNightRel * 100).toFixed(0)}
                onChange={(e) => handleDaypartChange("cvrNightRel", normalizeNumber(e.target.value) / 100)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* DSP */}
      {reco.dspEligible && (
        <Card className="border-primary">
          <CardHeader>
            <CardTitle className="text-lg">Escalade DSP conseillée</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Votre budget permet d'envisager DSP — audiences in market et vues produit — SoV stable et TACoS sous contrôle requis
            </p>
          </CardContent>
        </Card>
      )}

      {/* Section 6 — Watchers */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Watchers — Automatisation décisionnelle</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {watchers.map((w) => (
              <div key={w.id} className="flex items-start gap-3 p-3 border rounded-lg">
                <Switch
                  id={w.id}
                  checked={w.active}
                  onCheckedChange={() => toggleWatcher(w.id)}
                />
                <div className="flex-1">
                  <p className="text-sm font-semibold">{w.condition}</p>
                  <p className="text-xs text-muted-foreground">→ {w.action}</p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Glossaire */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Glossaire</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
            {GLOSSAIRE.map((entry, i) => (
              <div key={i} className="text-sm">
                <dt className="font-semibold text-primary inline">{entry.term}</dt>
                <dd className="inline ml-2 text-muted-foreground">{entry.definition}</dd>
              </div>
            ))}
          </dl>
        </CardContent>
      </Card>
    </div>
  );
};
